import {
  Button,
  Flex,
  FormFeedback,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useConfiguredForm,
} from '../index';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { Fragment, PropsWithChildren, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import { get, isNil, parseInt } from 'lodash-es';
import { FORM_MODE, TFunction } from '@wyz/types';

type Item<TValue extends string | number> = {
  id: string;
  label: string;
  value: TValue;
  rank: number;
};

type DraggableItemData<TValue extends string | number> = {
  item: Item<TValue>;
  index: number;
  origin: DroppableId | null | undefined;
};

type Props<TValue extends string | number, TKeys> = {
  name: string;
  items: Omit<Item<TValue>, 'id' | 'rank'>[];
  availableItemsLabel: string;
  selectedItemsLabel: string;
  ranking?: {
    enabled: boolean;
    allowExclusion?: boolean;
    maxItemsByRank?: number;
  };
  allowSelectAll?: boolean;
  translate: TFunction<TKeys>;
  translationKeys?: {
    resetRank?: keyof TKeys;
    deleteRank?: keyof TKeys;
    addRank?: keyof TKeys;
    excluded?: keyof TKeys;
    rank?: keyof TKeys;
    selectAll?: keyof TKeys;
  };
};

const EXCLUDED_RANK = -1;
const INITIAL_RANKS = 4;

function ItemDragAndDropSelector<TValue extends string | number, TKeys>({
  name,
  items,
  availableItemsLabel,
  selectedItemsLabel,
  ranking,
  translate,
  translationKeys: customTranslationKeys,
  allowSelectAll = false,
}: Props<TValue, TKeys>) {
  const withRanking = ranking?.enabled ?? false;

  const translationKeys = {
    resetRank:
      customTranslationKeys?.resetRank ??
      ('app.common.item_ranking.reset_rank' as keyof TKeys),
    deleteRank:
      customTranslationKeys?.deleteRank ??
      ('app.common.item_ranking.delete_rank' as keyof TKeys),
    addRank:
      customTranslationKeys?.addRank ??
      ('app.common.item_ranking.add_rank' as keyof TKeys),
    excluded:
      customTranslationKeys?.excluded ??
      ('app.common.item_ranking.excluded' as keyof TKeys),
    rank:
      customTranslationKeys?.rank ??
      ('app.common.item_ranking.rank' as keyof TKeys),
    selectAll:
      customTranslationKeys?.selectAll ??
      ('app.common.buttons.select_all' as keyof TKeys),
  };

  const methods = useFormContext();
  const formConfig = useConfiguredForm();
  const isEditMode = formConfig.mode === FORM_MODE.WRITE;

  const fieldArray = useFieldArray({
    control: methods.control,
    name,
  });

  const fields = fieldArray.fields as Item<TValue>[];

  const [lastRank, setLastRank] = useState(() => {
    const ranks = fields.map((field) => field.rank).filter(Boolean);
    const max = ranks.length > 0 ? Math.max(...ranks) : 0;
    return max === 0 && isEditMode ? INITIAL_RANKS : max;
  });

  const [itemDragged, setItemDragged] = useState<
    DraggableItemData<TValue> | undefined
  >();

  function onDragStart(event: DragStartEvent) {
    const itemData = event.active.data.current as DraggableItemData<TValue>;

    setItemDragged(itemData);
  }

  const fieldsByRank: {
    ranked: Array<Array<{ item: Item<TValue>; fieldIndex: number }>>;
    excluded: Array<{ item: Item<TValue>; fieldIndex: number }>;
  } = useMemo(() => {
    const ranked: Array<Array<{ item: Item<TValue>; fieldIndex: number }>> =
      Array(lastRank)
        .fill(undefined)
        .map(() => []);
    const excluded: Array<{ item: Item<TValue>; fieldIndex: number }> = [];

    if (!withRanking) {
      return { ranked, excluded: [] };
    }

    fields.forEach((field, index) => {
      if (field.rank !== EXCLUDED_RANK) {
        ranked[field.rank - 1].push({ item: field, fieldIndex: index });
      } else {
        excluded.push({ item: field, fieldIndex: index });
      }
    });

    return { ranked, excluded };
  }, [fields, lastRank, withRanking]);

  function onDragEnd(event: DragEndEvent) {
    const { item, index, origin } = event.active.data
      .current as DraggableItemData<TValue>;
    const targetId = event.over?.id as DroppableId | null | undefined;

    let rank = 1;
    if (targetId?.startsWith('SELECTED-')) {
      rank =
        targetId === 'SELECTED-EXCLUDED'
          ? EXCLUDED_RANK
          : parseInt(targetId?.split('-')[1]);
    }

    if (
      targetId?.startsWith('SELECTED') &&
      origin === 'AVAILABLE' &&
      (isNil(ranking?.maxItemsByRank) ||
        fieldsByRank.ranked[rank - 1].length < ranking.maxItemsByRank)
    ) {
      fieldArray.append({ ...item, rank });
    } else if (targetId === 'AVAILABLE' && origin?.startsWith('SELECTED')) {
      fieldArray.remove(index);
    }

    setItemDragged(undefined);
  }

  const availableItems = items.filter(
    (item) => !fields.some((selectedItem) => selectedItem.value === item.value),
  );

  return (
    <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <Flex className='flex-1 overflow-y-auto'>
        {isEditMode && (
          <Flex
            direction='column'
            className='border-end border-dotted p-2 w-50 overflow-y-auto'
          >
            <Text
              weight='semi-bold'
              color='tertiary'
              className='mb-2 text-uppercase text-center'
            >
              {availableItemsLabel}
            </Text>
            {allowSelectAll && (
              <Button
                id='select-all'
                variant='primary'
                onClick={() => fieldArray.append(availableItems)}
                size='sm'
                className='my-2'
              >
                {translate(translationKeys.selectAll)}
              </Button>
            )}
            <Droppable
              id='AVAILABLE'
              translate={translate}
              translationKeys={translationKeys}
            >
              {availableItems.map((item, index) => (
                <div
                  key={item.value}
                  className={clsx({
                    invisible: item.value === itemDragged?.item.value,
                  })}
                >
                  <Draggable
                    item={item}
                    index={index}
                    droppableId='AVAILABLE'
                  />
                </div>
              ))}
            </Droppable>
          </Flex>
        )}
        <Flex
          direction='column'
          className={clsx('p-2 overflow-y-auto', isEditMode ? 'w-50' : 'w-100')}
        >
          {isEditMode && (
            <Text
              weight='semi-bold'
              color='tertiary'
              className='mb-2 text-uppercase text-center'
            >
              {selectedItemsLabel}
            </Text>
          )}
          {withRanking ? (
            <>
              {fieldsByRank.ranked.map((rankItems, index) => {
                const rank = index + 1;
                return (
                  <Fragment key={rank}>
                    <Flex>
                      <Droppable
                        id={`SELECTED-${rank}`}
                        withRanking={withRanking}
                        rank={rank}
                        translate={translate}
                        translationKeys={translationKeys}
                      >
                        {rankItems.map(({ item, fieldIndex }) => (
                          <div
                            key={item.value}
                            className='bg-light py-1 px-2 rounded d-flex align-items-center gap-1'
                          >
                            {isEditMode && (
                              <div>
                                <Icon
                                  icon='close-circle'
                                  size='xxs'
                                  className='cursor-pointer'
                                  onClick={() => fieldArray.remove(fieldIndex)}
                                />
                              </div>
                            )}
                            <Text heading='xs'>{item.label}</Text>
                          </div>
                        ))}
                      </Droppable>
                      {isEditMode && (
                        <Flex direction='column' justifyContent='center'>
                          <Tooltip label={translate(translationKeys.resetRank)}>
                            <div
                              className='bg-white rounded cursor-pointer'
                              onClick={() =>
                                methods.setValue(
                                  name,
                                  fields.filter((item) => item.rank !== rank),
                                )
                              }
                            >
                              <Icon icon='reload' size='xxs' />
                            </div>
                          </Tooltip>
                          <Tooltip
                            label={translate(translationKeys.deleteRank)}
                          >
                            <div
                              className='bg-white rounded cursor-pointer'
                              onClick={() => {
                                methods.setValue(
                                  name,
                                  fields
                                    .filter((item) => item.rank !== rank)
                                    .map((item) =>
                                      item.rank > rank
                                        ? { ...item, rank: item.rank - 1 }
                                        : item,
                                    ),
                                );
                                setLastRank(lastRank - 1);
                                methods.clearErrors(`${name}.ranks.${rank}`);
                              }}
                            >
                              <Icon icon='trash' size='xxs' />
                            </div>
                          </Tooltip>
                        </Flex>
                      )}
                    </Flex>
                    {!isNil(
                      (
                        get(methods.formState.errors, name) as
                          | { ranks: Array<{ message: string }> }
                          | undefined
                      )?.ranks?.[rank]?.message,
                    ) && (
                      <FormFeedback invalid id={`${name}.ranks.${rank}.error`}>
                        {translate(
                          ((
                            get(methods.formState.errors, name) as
                              | { ranks: Array<{ message: string }> }
                              | undefined
                          )?.ranks?.[rank]?.message || '') as keyof TKeys,
                        )}
                      </FormFeedback>
                    )}
                  </Fragment>
                );
              })}
              {isEditMode && (
                <IconButton
                  id='add-rank'
                  icon='add-line'
                  iconAlign='right'
                  variant='primary'
                  size='xs'
                  onClick={() => setLastRank(lastRank + 1)}
                >
                  {translate(translationKeys.addRank)}
                </IconButton>
              )}

              {ranking?.allowExclusion && (
                <>
                  <Text
                    weight='semi-bold'
                    color='tertiary'
                    className='my-2 text-uppercase text-center'
                  >
                    {translate(translationKeys.excluded)}
                  </Text>
                  <Droppable
                    id='SELECTED-EXCLUDED'
                    withRanking={withRanking}
                    rank={EXCLUDED_RANK}
                    translate={translate}
                    translationKeys={translationKeys}
                  >
                    {fieldsByRank.excluded.map(({ item, fieldIndex }) => (
                      <div
                        key={item.value}
                        className='bg-light py-1 px-2 rounded d-flex align-items-center gap-1'
                      >
                        {isEditMode && (
                          <div>
                            <Icon
                              icon='close-circle'
                              size='xxs'
                              className='cursor-pointer'
                              onClick={() => fieldArray.remove(fieldIndex)}
                            />
                          </div>
                        )}
                        <Text heading='xs'>{item.label}</Text>
                      </div>
                    ))}
                  </Droppable>
                </>
              )}
            </>
          ) : (
            <Droppable
              id='SELECTED'
              translate={translate}
              translationKeys={translationKeys}
            >
              <Flex
                direction={isEditMode ? 'column' : 'row'}
                wrap={isEditMode ? undefined : 'wrap'}
                className={clsx({ 'gap-1': !isEditMode })}
              >
                {fields.map((item, index) => (
                  <div
                    key={item.id}
                    className={clsx({
                      invisible: item.value === itemDragged?.item.value,
                    })}
                  >
                    <Draggable
                      item={item}
                      index={index}
                      droppableId='SELECTED'
                    />
                  </div>
                ))}
              </Flex>
            </Droppable>
          )}
        </Flex>
      </Flex>
      <DragOverlay>
        {itemDragged ? (
          <Item
            item={itemDragged.item}
            droppableId={itemDragged.origin ?? 'SELECTED'}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function Item<TValue extends string | number>({
  item,
  droppableId,
}: {
  item: Omit<Item<TValue>, 'id' | 'rank'>;
  droppableId: DroppableId;
}) {
  const isAvailable = droppableId === 'AVAILABLE';

  return (
    <div
      className={clsx(
        'py-2 pe-3 ps-1 mb-1 d-flex align-items-center gap-2 rounded',
        {
          'bg-white': isAvailable,
          'bg-info-text': !isAvailable,
        },
      )}
    >
      <div>
        <Icon icon='drag-handle' color={isAvailable ? 'secondary' : 'white'} />
      </div>
      <Text
        weight='bold'
        color={isAvailable ? 'secondary' : 'white'}
        className='text-truncate'
      >
        {item.label}
      </Text>
    </div>
  );
}

function Draggable<TValue extends string | number>({
  item,
  droppableId,
  index,
}: {
  item: Omit<Item<TValue>, 'id' | 'rank'>;
  droppableId: DroppableId;
  index: number;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `${droppableId}-${item.value}`,
    data: { item, index, origin: droppableId },
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}>
      <Item item={item} droppableId={droppableId} />
    </div>
  );
}

type DroppableId =
  | 'AVAILABLE'
  | 'SELECTED'
  | `SELECTED-${number}`
  | 'SELECTED-EXCLUDED';

function Droppable<TKeys>({
  id,
  withRanking = false,
  rank,
  translate,
  translationKeys,
  children,
}: PropsWithChildren<{
  id: DroppableId;
  withRanking?: boolean;
  rank?: number;
  translate: TFunction<TKeys>;
  translationKeys: {
    resetRank: keyof TKeys;
    deleteRank: keyof TKeys;
    addRank: keyof TKeys;
    excluded: keyof TKeys;
    rank: keyof TKeys;
  };
}>) {
  const { setNodeRef } = useDroppable({ id });
  const formConfig = useConfiguredForm();
  const isEditMode = formConfig.mode === FORM_MODE.WRITE;

  return (
    <div
      ref={withRanking ? undefined : setNodeRef}
      className={clsx('p-2 w-100', {
        'flex-1 overflow-y-auto': !withRanking,
        'd-flex align-items-center gap-1': withRanking,
      })}
    >
      {withRanking ? (
        <>
          <Text heading='sm' color='tertiary' className='text-nowrap'>
            {rank !== EXCLUDED_RANK && (
              <>{translate(translationKeys.rank, { rank })}</>
            )}
          </Text>
          <div
            ref={withRanking ? setNodeRef : undefined}
            className={clsx('rounded p-2 flex-1 d-flex gap-1 flex-wrap w-100', {
              'bg-white': isEditMode,
            })}
            style={{ minHeight: 42 }}
          >
            {children}
          </div>
        </>
      ) : (
        children
      )}
    </div>
  );
}

export default ItemDragAndDropSelector;
